import React, { useState, useEffect } from 'react';
import '../components/DBOSHomepage.css';
import SiteHeader from '../components/SiteHeader';
import './SmartWeb.css';

const SmartWeb = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    comment: '',
    termsAccepted: false,
    newsletterOptIn: false,
  });
  const [navOpen, setNavOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(true);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.termsAccepted) {
      alert('Please accept the terms and privacy policy to continue');
      return;
    }
    console.log('Form submitted:', formData);
    alert('Thank you! We will contact you soon.');
    setFormData({
      name: '',
      email: '',
      mobile: '',
      comment: '',
      termsAccepted: false,
      newsletterOptIn: false,
    });
  };


  useEffect(() => {
    const updateOpenStatus = () => {
      const now = new Date();
      const dublinTime = new Date(now.toLocaleString("en-US", {timeZone: "Europe/Dublin"}));
      const day = dublinTime.getDay();
      const minutes = dublinTime.getHours() * 60 + dublinTime.getMinutes();
      const isOpenNow = (
        (day >= 1 && day <= 4 && minutes >= 540 && minutes < 1080) ||
        (day === 5 && minutes >= 540 && minutes < 720)
      );
      const indicator = document.querySelector('.status-indicator');
      const hoursText = document.querySelector('.hours-text');
      const hoursContainer = document.querySelector('.opening-hours');
      if (indicator && hoursText && hoursContainer) {
        if (isOpenNow) {
          indicator.className = 'status-indicator open';
          hoursText.textContent = 'OPEN';
          hoursContainer.classList.add('status-open');
          hoursContainer.classList.remove('status-closed');
        } else {
          indicator.className = 'status-indicator closed';
          hoursText.textContent = 'CLOSED';
          hoursContainer.classList.remove('status-open');
          hoursContainer.classList.add('status-closed');
        }
      }
    };
    updateOpenStatus();
    const interval = setInterval(updateOpenStatus, 60000);
    return () => clearInterval(interval);
  }, []);
  return (
    <div className="smartweb-page">
      <SiteHeader active="solutions" />

      <main className="smartweb-content">
        <section className="hero-section">
          <div className="container">
            <h1 className="page-title">
              Your Website Should Work
              <span className="gold-gradient shimmering"> As Hard As You Do</span>
            </h1>
            <p className="hero-lead shimmering">
              Fast, reliable, easy to manage—your website, your way, your control.
            </p>
          </div>
        </section>

        <section className="content-section">
          <div className="container">
            <h2>The Challenge SMEs Face Today</h2>
            <p>
              Across Ireland and beyond, we see the same story: slow-loading websites, broken booking systems, 
              SSL certificate errors, outdated WordPress installations with dozens of conflicting plugins, 
              incorrect opening hours on Google Business Profile, missing WhatsApp buttons, 404 errors on 
              critical pages, and websites that don't work on mobile phones—in 2026.
            </p>
            <p>
              Your website should be your hardest-working employee. Instead, it's often losing customers 
              before they even reach out. Inconsistent information across Google, Facebook, and Instagram. 
              Broken links to content that no longer exists. Unclear calls-to-action. No easy way to call, 
              navigate, or book. The result? Customers give up and go elsewhere.
            </p>
          </div>
        </section>

        <section className="content-section solution-section">
          <div className="container">
            <h2>The SmartWeb Solution</h2>
            <p className="lead-text">
              SmartWeb gives you a modern, fast, mobile-first website you can actually manage—through 
              your DBOS portal. No technical degree required. No waiting days for an agency to make a 
              simple update. No outdated plugins breaking your site at 3 AM.
            </p>
            <p>
              Built on modern infrastructure, optimized for speed and search engines, and designed to 
              work seamlessly on every device. Your content, your shop, your bookings—all in one place, 
              accessible whenever you need them.
            </p>
            <p>
              Best of all? You're not alone. When you need help crafting that perfect blog post or 
              setting up a new product category, assistance is just a click away. We handle the technical 
              complexity so you can focus on running your business.
            </p>
          </div>
        </section>

        <section className="features-section">
          <div className="container">
            <h2>What Makes SmartWeb Different</h2>
            <div className="features-grid">
              
              <div className="feature-card">
                <h3>Speed & Mobile-First Design</h3>
                <p>
                  Your customers won't wait 15 seconds for your site to load. SmartWeb sites are 
                  built for speed—under 2 seconds on mobile, every time. Fast loading means better 
                  search rankings, more visitors staying on your site, and more conversions.
                </p>
                <p>
                  Every SmartWeb site is mobile-first. That means it looks perfect and works flawlessly 
                  on phones, tablets, and desktops. Your content adapts automatically. Images load quickly. 
                  Buttons are easy to tap. Navigation makes sense.
                </p>
                <ul>
                  <li>Load times under 2 seconds</li>
                  <li>Perfect display on all devices</li>
                  <li>SEO-optimized out of the box</li>
                  <li>Secure SSL certificates included</li>
                </ul>
              </div>

              <div className="feature-card">
                <h3>Easy Content Management</h3>
                <p>
                  Update your website directly from your DBOS portal. Add a new blog post, update your 
                  opening hours, announce a special offer—all without touching code or waiting for a 
                  developer. Changes go live immediately.
                </p>
                <p>
                  Your News and Blog sections are fully under your control. Write, edit, schedule, 
                  publish. Add images, format text, embed videos. Need help with writer's block? 
                  Our content assistants can help you draft, refine, and polish your message.
                </p>
                <ul>
                  <li>Intuitive editing interface</li>
                  <li>Publish instantly or schedule for later</li>
                  <li>Content assistance when you need it</li>
                  <li>No technical knowledge required</li>
                </ul>
              </div>

              <div className="feature-card">
                <h3>Ecommerce & Bookings Ready</h3>
                <p>
                  Sell products, take bookings, accept payments—all integrated into your website and 
                  your DBOS portal. Add products, set prices, manage inventory, fulfill orders. No 
                  juggling multiple systems or logging into five different platforms.
                </p>
                <p>
                  Integrated payment processing means your customers can pay securely with cards, 
                  digital wallets, or local payment methods. Orders flow directly into your portal 
                  where you can track, fulfill, and manage everything in one place.
                </p>
                <ul>
                  <li>Integrated payment processing</li>
                  <li>Inventory management</li>
                  <li>Order tracking and fulfillment</li>
                  <li>Booking and appointment systems</li>
                </ul>
              </div>

              <div className="feature-card">
                <h3>Always Up-to-Date & Secure</h3>
                <p>
                  Forget about plugin updates, security patches, and compatibility issues. SmartWeb 
                  handles all technical maintenance automatically. Your site stays secure, fast, and 
                  up-to-date without you lifting a finger.
                </p>
                <p>
                  Regular backups mean your content is never lost. Automatic updates mean you're 
                  protected from security vulnerabilities. Uptime monitoring means we catch issues 
                  before your customers do.
                </p>
                <ul>
                  <li>Automatic security updates</li>
                  <li>Daily backups</li>
                  <li>99.9% uptime guarantee</li>
                  <li>Technical support included</li>
                </ul>
              </div>

            </div>
          </div>
        </section>

        <section className="content-section how-it-works">
          <div className="container">
            <h2>How SmartWeb Works</h2>
            <div className="steps-grid">
              <div className="step-card">
                <div className="step-number">1</div>
                <h3>Digital Health Assessment</h3>
                <p>
                  We start with a conversation. What's working? What's not? What do you need your 
                  website to do? We assess your current situation and identify the opportunities.
                </p>
              </div>
              <div className="step-card">
                <div className="step-number">2</div>
                <h3>Build Your SmartWeb</h3>
                <p>
                  Our team builds your website based on your needs. Modern design, fast loading, 
                  mobile-first, search-optimized. We handle the technical details while you focus 
                  on your business.
                </p>
              </div>
              <div className="step-card">
                <div className="step-number">3</div>
                <h3>Launch & Manage</h3>
                <p>
                  Your SmartWeb goes live. You get full access to your DBOS portal to manage content, 
                  products, bookings, and more. We're here when you need help, but you're in control.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section">
          <div className="container">
            <h2>What You Can Manage Yourself</h2>
            <p>
              Your DBOS portal gives you direct control over the parts of your website that change 
              regularly. No waiting for developers. No technical headaches.
            </p>
            <ul className="capabilities-list">
              <li><strong>News & Blog:</strong> Write, edit, schedule, and publish content</li>
              <li><strong>Products & Shop:</strong> Add products, set prices, manage inventory</li>
              <li><strong>Orders & Bookings:</strong> View, track, and fulfill customer orders</li>
              <li><strong>Opening Hours:</strong> Update hours across your website and Google Business Profile</li>
              <li><strong>Contact Information:</strong> Keep phone numbers, addresses, and social links current</li>
              <li><strong>Special Offers:</strong> Announce promotions, events, or seasonal changes</li>
            </ul>
          </div>
        </section>

        <section className="content-section help-section">
          <div className="container">
            <h2>Where We Help</h2>
            <p>
              You manage the day-to-day. We handle the technical complexity and provide support 
              when you need it.
            </p>
            <ul className="help-list">
              <li><strong>Initial Setup & Design:</strong> We build your SmartWeb from scratch</li>
              <li><strong>Technical Maintenance:</strong> Security, updates, backups, uptime monitoring</li>
              <li><strong>Content Assistance:</strong> Stuck on a blog post? Need help with product descriptions? We've got you</li>
              <li><strong>Strategic Guidance:</strong> SEO advice, conversion optimization, feature recommendations</li>
              <li><strong>Integration Support:</strong> Connect your website with booking systems, payment processors, or other tools</li>
            </ul>
          </div>
        </section>

        <section className="content-section faq-section">
          <div className="container">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-grid">
              <div className="faq-item">
                <h3>Can I update my website myself?</h3>
                <p>
                  Yes! Your DBOS portal gives you full control over News, Blog, and Shop content. 
                  You can publish updates instantly without any technical knowledge.
                </p>
              </div>
              <div className="faq-item">
                <h3>How fast will my website load?</h3>
                <p>
                  SmartWeb sites are optimized for speed—under 2 seconds on mobile. Fast loading 
                  means better search rankings and happier customers.
                </p>
              </div>
              <div className="faq-item">
                <h3>What if I need help with content?</h3>
                <p>
                  Content assistance is available when you need it. Whether you're stuck on a blog 
                  post or need help with product descriptions, we're here to help.
                </p>
              </div>
              <div className="faq-item">
                <h3>Can I sell products and take bookings?</h3>
                <p>
                  Absolutely. SmartWeb includes integrated ecommerce and booking systems. Accept 
                  payments, manage inventory, fulfill orders—all from your DBOS portal.
                </p>
              </div>
              <div className="faq-item">
                <h3>What about mobile devices?</h3>
                <p>
                  Every SmartWeb site is mobile-first. Your website will look perfect and work 
                  flawlessly on phones, tablets, and desktops.
                </p>
              </div>
              <div className="faq-item">
                <h3>How do I get started?</h3>
                <p>
                  Start with a Digital Health Assessment. Fill out the form below and we'll 
                  schedule a conversation to understand your needs and show you what's possible.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="cta-section">
          <div className="container">
            <h2>Ready to Get Started?</h2>
            <p className="cta-lead">
              Start with your Digital Health Assessment. We'll review your current situation and 
              show you what SmartWeb can do for your business.
            </p>
            
            <form className="assessment-form" onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="name">Your Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="mobile">Mobile Number</label>
                  <input
                    type="tel"
                    id="mobile"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Optional"
                  />
                </div>
              </div>
              
              <div className="form-group full-width">
                <label htmlFor="comment">Tell Us About Your Project</label>
                <textarea
                  id="comment"
                  name="comment"
                  value={formData.comment}
                  onChange={handleChange}
                  maxLength={1500}
                  rows={5}
                  className="form-textarea"
                  placeholder="What challenges are you facing? What do you need your website to do? (Optional, max 1500 characters)"
                />
                <div className="char-count">{formData.comment.length} / 1500</div>
              </div>
              
              <div className="form-checkboxes">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="termsAccepted"
                    checked={formData.termsAccepted}
                    onChange={handleChange}
                    required
                  />
                  <span>I accept the <a href="/legal/terms">terms</a> & <a href="/legal/privacy">privacy policy</a></span>
                </label>
              </div>
              
              <button type="submit" className="btn-primary shimmering-btn">
                Start Building Your Digital Trust
                <span className="btn-arrow">→</span>
              </button>
            </form>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="container">
          <p className="copyright">© 2026 DBOS. Ireland (CRO Registered). All rights reserved.</p>
          <div className="footer-contact">
            <a href="mailto:hello@dbos.world">hello@dbos.world</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SmartWeb;
